import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

export function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="June 1, 2026"
      summary="What we collect, why we collect it, and the controls you have over your data. Plain-English version up top — full text below."
    >
      <h2>Quick summary</h2>
      <ul>
        <li>We only collect what we need to run Rynora — account email, run logs, and billing details.</li>
        <li>We never sell your data. We never train external models on your test logs.</li>
        <li>
          Every Rynora SaaS organisation runs in the <strong>US-PHX</strong> region today (Oracle Cloud Infrastructure,
          us-phoenix-1). An EU-Frankfurt region is in design-partner alpha; see{' '}
          <Link to="/trust/regions">/trust/regions</Link> for the current status and ETA.
        </li>
        <li>
          File an access / deletion / portability request through the public{' '}
          <Link to="/privacy/data-request">data-request portal</Link>, or email{' '}
          <a href="mailto:privacy@rynora.app">privacy@rynora.app</a> directly.
        </li>
      </ul>

      <h2>1. Who we are</h2>
      <p>
        Rynora ("we", "us") operates the rynora.app service. You can reach the privacy contact at{' '}
        <a href="mailto:support@rynora.app">support@rynora.app</a>.
      </p>

      <h2>2. What we collect</h2>
      <h3>2.1 Account data</h3>
      <p>Name, email, hashed password, organization, and your chosen plan.</p>
      <h3>2.2 Usage data</h3>
      <p>
        Test flows you create, the run history, screenshots / videos / DOM traces collected during runs, browser
        metadata, and timing information. This is the data your tests need to be useful — losing it would defeat the
        purpose.
      </p>
      <h3>2.3 Operational telemetry</h3>
      <p>
        Errors via Sentry, uptime via Better Stack, anonymous traffic via the access log. We hash IP addresses with a
        per-instance salt before storing.
      </p>
      <h3>2.4 Billing</h3>
      <p>Stripe handles all card data — we only see the customer ID, plan, and last-4 of the card.</p>

      <h2>3. Why we collect it</h2>
      <p>
        To operate the service, troubleshoot incidents, bill correctly, and meet legal obligations. We do not use your
        data for advertising. We do not train AI models on your test runs.
      </p>

      <h2>4. Where it lives</h2>
      <p>
        Today, every Rynora SaaS organisation lives in our <strong>US-PHX</strong> region (Oracle Cloud Infrastructure,
        us-phoenix-1). The primary database, runner artefacts, and admin console all run in that single tenancy. Backups
        go to Backblaze B2 (US-West) and an S3-compatible bucket in <code>eu-north-1</code> (Stockholm). Email goes
        through Cloudflare (inbound) and Resend (outbound transactional).
      </p>
      <p>
        EU and UK customer transfers are covered by the EU Standard Contractual Clauses 2021 + the UK IDTA, both
        documented in our DPA. The <Link to="/trust/regions">/trust/regions</Link> page lists every region Rynora plans
        to operate in, with current status (GA, design-partner alpha, or planned) and the sub-processor split for each.
        The EU-Frankfurt region is the next one we plan to ship; it will run a parallel set of EU sub-processors so that
        no customer data leaves the EU once it is generally available.
      </p>

      <h2>5. How long we keep it</h2>
      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>Retention</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Account data</td>
            <td>Lifetime of the account, then 30 days after deletion.</td>
          </tr>
          <tr>
            <td>Run artefacts (free tier)</td>
            <td>30 days.</td>
          </tr>
          <tr>
            <td>Run artefacts (paid tier)</td>
            <td>Per the org's data-retention setting (default 90 days).</td>
          </tr>
          <tr>
            <td>Audit log</td>
            <td>2 years.</td>
          </tr>
          <tr>
            <td>Billing records</td>
            <td>7 years (legal requirement).</td>
          </tr>
        </tbody>
      </table>

      <h2>6. Your rights</h2>
      <p>
        You can export, correct, or delete your data from the org settings page. For GDPR / CCPA requests, email{' '}
        <a href="mailto:support@rynora.app">support@rynora.app</a> and we will respond within 30 days.
      </p>

      <h2>7. Sub-processors</h2>
      <ul>
        <li>Oracle Cloud — primary hosting</li>
        <li>Backblaze B2 — encrypted backups</li>
        <li>Cloudflare — DNS, WAF, inbound email routing</li>
        <li>Resend — outbound transactional email</li>
        <li>Stripe — billing and payments</li>
        <li>Sentry / Better Stack / Grafana Cloud — observability</li>
      </ul>

      <h2>8. Contact</h2>
      <p>
        Privacy questions: <a href="mailto:support@rynora.app">support@rynora.app</a>
        <br />
        Security disclosures: <a href="mailto:ops@rynora.app">ops@rynora.app</a>
      </p>
    </LegalLayout>
  );
}
